"use client"

import { ProjectConfig } from "./ProjectConfigurator"
import { QuestionCard } from "./QuestionCard"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/typography"
import { Textarea } from "@/components/ui/textarea"

interface AdditionalInfoProps {
  config: ProjectConfig
  onUpdate: (updates: Partial<ProjectConfig>) => void
  onNext: () => void
  onBack: () => void
}

export function AdditionalInfo({ config, onUpdate, onNext, onBack }: AdditionalInfoProps) {
  const additional = config.additional || {}

  return (
    <div className="space-y-8">
      <div>
        <Typography variant="h2" as="h2" className="mb-4">
          Additional Information
        </Typography>
        <Typography variant="body" className="text-tk-slate/80">
          Help me understand your timeline and any other details.
        </Typography>
      </div>

      <div>
        <Typography variant="h3" as="h3" className="mb-4">
          Timeline?
        </Typography>
        <div className="grid gap-3 sm:grid-cols-2">
          {["ASAP", "1-3 months", "3-6 months", "Flexible"].map((option) => (
            <QuestionCard
              key={option}
              label={option}
              selected={additional.timeline === option}
              onClick={() => {
                const currentAdditional = config.additional || {}
                onUpdate({ additional: { ...currentAdditional, timeline: option } })
              }}
            />
          ))}
        </div>
      </div>

      <div>
        <Typography variant="h3" as="h3" className="mb-4">
          Budget range? (Optional)
        </Typography>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Under $10k",
            "$10k - $25k",
            "$25k - $50k",
            "$50k+",
            "Prefer not to say",
          ].map((option) => (
            <QuestionCard
              key={option}
              label={option}
              selected={additional.budget === option}
              onClick={() => {
                const currentAdditional = config.additional || {}
                onUpdate({ additional: { ...currentAdditional, budget: option } })
              }}
            />
          ))}
        </div>
      </div>

      <div>
        <Typography variant="h3" as="h3" className="mb-4">
          Anything else I should know?
        </Typography>
        <Textarea
          placeholder="Additional context, specific requirements, or questions..."
          value={additional.anythingElse || ""}
          onChange={(e) => {
            const currentAdditional = config.additional || {}
            onUpdate({ additional: { ...currentAdditional, anythingElse: e.target.value } })
          }}
          className="min-h-[120px] border-tk-slate/20 bg-tk-linen"
        />
      </div>

      <div className="flex justify-between pt-6">
        <Button
          onClick={onBack}
          variant="outline"
          className="rounded-full border-tk-slate/25 bg-tk-linen text-sm text-tk-slate hover:border-tk-teal hover:text-tk-onyx font-ui"
        >
          ← Back
        </Button>
        <Button
          onClick={onNext}
          className="rounded-full bg-tk-teal px-8 py-2 text-sm font-semibold text-tk-linen hover:bg-tk-teal/90 font-ui"
        >
          Continue →
        </Button>
      </div>
    </div>
  )
}

